// Adapted from apps/admin/src/pages/dashboard/gps-tracking/components/vehicle-list.tsx (PRD #875):
// adds the loading, error and filtered-out states and the card of a linked truck that is not in the
// period's list.
import React, { memo } from "react";
import type { ConnectProto } from "@racoon/api";
import { Button, cn } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import type { FleetTruck } from "../model/fleet";
import { VehicleFilters } from "./vehicle-filters";
import { VehicleCard } from "./vehicle-card";

interface VehicleListProps {
  vehicles: FleetTruck[];
  /** The period has routes at all, so an empty list means the search or filter hid them. */
  hasRoutes: boolean;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  day: Date;
  onDayChange: (day: Date) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
  selectedVehicleId?: string;
  onVehicleSelect: (vehicleId: string) => void;
  /** The selected truck is not in the list; null while its card has not loaded. */
  offListTruck?: ConnectProto.GovTruckInfo | null;
}

const VehicleListComponent: React.FC<VehicleListProps> = ({
  vehicles,
  hasRoutes,
  loading,
  error,
  onRetry,
  searchQuery,
  onSearchChange,
  day,
  onDayChange,
  activeFilterCount,
  onOpenFilters,
  selectedVehicleId,
  onVehicleSelect,
  offListTruck,
}) => {
  const t = useT();
  const message = (testId: string, text: string) => (
    <div
      data-testid={testId}
      className="flex items-center justify-center h-full p-4 text-center text-sm text-gray-500"
    >
      {text}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-white border rounded-xl">
      {/* Filters Section */}
      <VehicleFilters
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        day={day}
        onDayChange={onDayChange}
        loading={loading}
        activeFilterCount={activeFilterCount}
        onOpenFilters={onOpenFilters}
      />

      {selectedVehicleId && offListTruck !== undefined && (
        <div className="px-2 pt-2">
          <div
            role="button"
            tabIndex={0}
            aria-pressed
            data-testid="gps-tracking-off-list"
            onClick={() => onVehicleSelect(selectedVehicleId)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onVehicleSelect(selectedVehicleId);
              }
            }}
            className="p-4 rounded-lg cursor-pointer border-2 border-brand-blue bg-brand-blue-light/40"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center rounded-md text-sm border border-text-primary px-1.5 py-0.5 font-medium">
                {offListTruck?.truck?.plateNumber ?? `#${selectedVehicleId}`}
              </span>
              <span className="truncate text-sm text-gray-700">{offListTruck?.truck?.model}</span>
            </div>
            {offListTruck?.serviceProvider?.name && (
              <div className="truncate text-sm font-medium text-slate-900">
                {offListTruck.serviceProvider.name}
              </div>
            )}
            <div className="mt-1 text-xs text-text-secondary">
              {t(Namespace.Govdash, "gpsTracking.offList")}
            </div>
          </div>
        </div>
      )}

      {/* Vehicle List */}
      <div className={cn("p-2 overflow-y-auto flex-1 min-h-0", loading && "opacity-60")}>
        {loading && vehicles.length === 0 ? (
          message("gps-tracking-list-loading", t(Namespace.Common, "loading"))
        ) : error ? (
          <div
            data-testid="gps-tracking-list-error"
            className="flex flex-col items-center justify-center gap-3 h-full p-4 text-center text-sm text-gray-500"
          >
            {t(Namespace.Govdash, "gpsTracking.loadError")}
            <Button
              variant="outline"
              size="sm"
              data-testid="gps-tracking-list-retry"
              onClick={onRetry}
            >
              {t(Namespace.Govdash, "gpsTracking.retry")}
            </Button>
          </div>
        ) : vehicles.length === 0 ? (
          // The district / MVO filter applies on the server, so with one set an empty
          // period cannot be told apart from a filter that matched nothing.
          hasRoutes || activeFilterCount > 0 ? (
            message("gps-tracking-list-not-found", t(Namespace.Govdash, "gpsTracking.nothingFound"))
          ) : (
            message("gps-tracking-list-empty", t(Namespace.Govdash, "gpsTracking.noRoutes"))
          )
        ) : (
          vehicles.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              isSelected={selectedVehicleId === vehicle.id}
              onSelect={onVehicleSelect}
            />
          ))
        )}
      </div>
    </div>
  );
};

export const VehicleList = memo(VehicleListComponent);
